// src/marketing/launch/NewLaunchDialog.tsx
import { useState } from 'react'
import type { LaunchPlan } from '../../lib/types'

type Props = {
  onClose: () => void
  onConfirm: (name: string, launchDate: string | null, duplicateOf?: string | null) => void
  isPending: boolean
  error: string | null
  existingPlans: LaunchPlan[]
}

export function NewLaunchDialog({ onClose, onConfirm, isPending, error, existingPlans }: Props) {
  const [name, setName] = useState('')
  const [launchDate, setLaunchDate] = useState('')
  const [duplicateOf, setDuplicateOf] = useState<string>('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    onConfirm(name.trim(), launchDate || null, duplicateOf || null)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Nouveau lancement"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '420px',
          maxWidth: '90vw',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', marginBottom: '1.25rem' }}>
          Nouveau lancement
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="launch-name"
              style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
            >
              Nom du lancement
            </label>
            <input
              id="launch-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex. Lancement V1"
              required
              style={{
                width: '100%',
                padding: '0.4375rem 0.625rem',
                background: 'var(--bg)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: 'var(--fg)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="launch-date"
              style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
            >
              Date D0 (optionnel)
            </label>
            <input
              id="launch-date"
              type="date"
              value={launchDate}
              onChange={(e) => setLaunchDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.4375rem 0.625rem',
                background: 'var(--bg)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: 'var(--fg)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {existingPlans.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="duplicate-of"
                style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '0.375rem' }}
              >
                Dupliquer depuis (optionnel)
              </label>
              <select
                id="duplicate-of"
                value={duplicateOf}
                onChange={(e) => setDuplicateOf(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.4375rem 0.625rem',
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  color: 'var(--fg)',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">— Utiliser le gabarit —</option>
                {existingPlans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          {error && (
            <p style={{ color: 'var(--arancio)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: '0.4375rem 0.875rem',
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: 'var(--fg)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim()}
              style={{
                padding: '0.4375rem 0.875rem',
                background: 'var(--arancio)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#fff',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {isPending ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
